/**
 * useAuth — React hook that exposes the current Supabase session/user.
 *
 *   const { user, session, loading, signOut } = useAuth();
 *
 * Returns `user: null` when nobody is signed in. The hook subscribes to
 * onAuthStateChange so any sign-in/out anywhere in the app updates components.
 */

import { useEffect, useState } from "react";
import { supabase, type Session, type User } from "./supabase";

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  async function signOut() {
    if (!supabase) return;
    await supabase.auth.signOut();
    setSession(null);
  }

  const user = (session?.user as User | null) ?? null;
  const metadata = (user?.user_metadata ?? {}) as Record<string, any>;

  // For COPPA: under-13 users play under a parent account. The parent's
  // sign-in is incomplete until they've gone through the consent screen,
  // which writes parental_consent_at into user_metadata.
  const userRole: "self" | "parent" | null = metadata.role ?? null;
  const hasParentalConsent = !!metadata.parental_consent_at;
  const kidNickname: string | null = metadata.kid_nickname ?? null;

  return {
    session,
    user,
    loading,
    signOut,
    isReady: !!supabase,
    userRole,
    hasParentalConsent,
    kidNickname,
  };
}
